// react
import React, { useEffect, useState } from "react";

// react-i18next
import { useTranslation } from "react-i18next";

// @mui
import useMediaQuery from "@mui/material/useMediaQuery";
import Box from "@mui/material/Box";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import InputLabel from "@mui/material/InputLabel";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";

// components
import { Puller } from "ui-components/Puller";
import { LoadingComponent } from "ui-components/LoadingComponent";

// databox-controller
import getTagTypes from "databox-controller/getTagTypes";
import { updateForwardRoute } from "databox-controller/manageForwardRoute";

// A ---------------------------------------------------------------------- M

const EditForwardRoute = ({ tag, route, open, setOpen }) => {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const { t } = useTranslation();

  const [endpoint, setEndpoint] = useState("");
  const [type, setType] = useState("");
  const [status, setStatus] = useState(true);
  const [types, setTypes] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [headers, setHeaders] = useState([{ key: "", value: "" }]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handlePopstate = () => {
      if (open) {
        setOpen(false);
      }
    };

    window.addEventListener("popstate", handlePopstate);

    return () => {
      window.removeEventListener("popstate", handlePopstate);
    };
  }, [open]);

  useEffect(() => {
    const fetch = async () => {
      const types = await getTagTypes(tag.id);
      setTypes(types);
    };

    fetch();
  }, [tag]);

  useEffect(() => {
    setEndpoint(route.endpoint);
    setType(route.type ? route.type : "");
    setStatus(route.enabled);
    setSelectedTypes(route.types);
    setHeaders(route.headers || [{ key: "", value: "" }]);
  }, [route]);

  const handleReset = () => {
    setHeaders(route.headers || [{ key: "", value: "" }]);
    setOpen(false);
  };

  const submitForwardRoute = async () => {
    setLoading(true);

    const body = {
      tag: tag,
      type: type,
      endpoint: endpoint,
      status: status,
      selectedTypes: selectedTypes,
      headers: headers,
    };

    await updateForwardRoute(tag.id, route.id, body);

    setLoading(false);
    setOpen(false);
  };

  const handleAddHeader = () => {
    setHeaders([...headers, { key: "", value: "" }]);
  };

  const handleRemoveHeader = (index) => {
    const newHeaders = headers.filter((_, idx) => idx !== index);
    setHeaders(newHeaders);
  };

  const handleHeaderChange = (index, event) => {
    const { name, value } = event.target;
    const newHeaders = headers.map((header, idx) => {
      if (idx === index) {
        return { ...header, [name]: value };
      }
      return header;
    });
    setHeaders(newHeaders);
  };

  const form = (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <TextField margin="dense" id="endpoint-edit" label={t("endpoint")} name="Endpoint" value={endpoint} onChange={(e) => setEndpoint(e.target.value)} fullWidth />
      </Grid>
      <Grid item xs={12}>
        <FormControl fullWidth>
          <InputLabel id="type-select-label-edit">{t("type")}</InputLabel>
          <Select labelId="type-select-label-edit" id="type-edit" label={t("type")} value={type} onChange={(e) => setType(e.target.value)}>
            <MenuItem value={"REST"}>REST</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <FormControl fullWidth>
          <InputLabel id="status-select-label-edit">{t("status")}</InputLabel>
          <Select labelId="status-select-label-edit" id="status-edit" label={t("status")} value={status} onChange={(e) => setStatus(e.target.value)}>
            <MenuItem value={true}>{t("enabled").toUpperCase()}</MenuItem>
            <MenuItem value={false}>{t("disabled").toUpperCase()}</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <FormControl fullWidth>
          <Select
            id="data-types-edit"
            displayEmpty
            multiple
            value={selectedTypes}
            onChange={(e) => setSelectedTypes(e.target.value)}
            renderValue={(selected) => {
              if (selected.length === 0) {
                return <Typography color="gray">{t("choose_datatypes")}</Typography>;
              }
              return selected.join(", ");
            }}
          >
            {types.length !== 0 &&
              types.map((type, index) => (
                <MenuItem key={index} value={type}>
                  <Checkbox checked={selectedTypes.indexOf(type) > -1} />
                  <ListItemText primary={type} />
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h6" color="gray" fontWeight="bold">
          Headers
        </Typography>
      </Grid>
      {headers.map((header, index) => (
        <Grid key={index} item container xs={12} spacing={1}>
          <Grid item xs={12}>
            <TextField label={t("key")} name="key" required value={header.key} onChange={(e) => handleHeaderChange(index, e)} fullWidth />
          </Grid>
          <Grid item xs={12}>
            <TextField label={t("value")} name="value" required value={header.value} onChange={(e) => handleHeaderChange(index, e)} fullWidth />
          </Grid>
          <Grid item container xs={12} justifyContent="flex-end">
            <Button variant="contained" sx={{ color: "white", bgcolor: "red" }} onClick={() => handleRemoveHeader(index)}>
              {t("remove")}
            </Button>
          </Grid>
        </Grid>
      ))}
      <Grid item xs={12}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddHeader} fullWidth>
          {t("add_header")}
        </Button>
      </Grid>
    </Grid>
  );

  return isMobile ? (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={handleReset}
      onOpen={() => setOpen(true)}
      sx={{
        "& .MuiDrawer-paper": {
          width: "100%",
          height: "90%",
          borderTopLeftRadius: "4%",
          borderTopRightRadius: "4%",
        },
      }}
    >
      <Puller />
      <Box sx={{ p: "5%" }}>
        <Grid container alignItems="center" justifyContent="center" mt="5%" mb="10%">
          <Grid item>
            <Typography variant="h6" fontWeight="bold">
              {t("edit_endpoint")}
            </Typography>
          </Grid>
        </Grid>
        {loading ? (
          <LoadingComponent />
        ) : (
          <>
            {form}
            <Grid container mt="3%">
              <Grid item xs={12}>
                <Button fullWidth variant="contained" startIcon={<EditIcon />} onClick={submitForwardRoute}>
                  {t("edit")}
                </Button>
              </Grid>
            </Grid>
          </>
        )}
      </Box>
    </SwipeableDrawer>
  ) : (
    <Dialog open={open} onClose={handleReset} maxWidth="md" fullWidth>
      <DialogTitle>
        <Grid container alignItems="center" justifyContent="space-between">
          <Grid item>
            <Typography variant="h5" fontWeight="bold">
              {t("edit_endpoint")}
            </Typography>
          </Grid>
          <Grid item>
            <IconButton onClick={handleReset} color="error" edge="end">
              <CloseOutlinedIcon />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>
      {loading ? (
        <LoadingComponent />
      ) : (
        <DialogContent>
          {form}
          <Grid item container xs={12} mt="3%">
            <Grid item xs={12}>
              <Button fullWidth variant="contained" startIcon={<EditIcon />} onClick={submitForwardRoute}>
                {t("edit")}
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
      )}
    </Dialog>
  );
};

export default EditForwardRoute;
